import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, Input } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BaseComponent, UtilsMisc } from '@pit/core';
import { IPinItTrackFrontendApplicationService, PinItTrackFrontendServicesAccessor } from '@pit/frontend-shared';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

@Component({
    selector: '[pin-it-track-web-app-menuitem]',
    templateUrl: './pin.it.track.web.app.menu.item.component.html',
    styleUrls: ['./pin.it.track.web.app.menu.item.component.scss'],
    host: {
        '[class.layout-root-menuitem]': 'isRoot || isActive',
        '[class.active-menuitem]': 'isActive'
    },
    animations: [
        trigger('children', [
            state('void', style({
                height: '0px',
                padding: '0px'
            })),
            state('hiddenAnimated', style({
                height: '0px',
                padding: '0px'
            })),
            state('visibleAnimated', style({
                height: '*'
            })),
            state('visible', style({
                height: '*'
            })),
            state('hidden', style({
                height: '0px',
                padding: '0px'
            })),
            transition('visibleAnimated => hiddenAnimated', animate('400ms cubic-bezier(0.86, 0, 0.07, 1)')),
            transition('hiddenAnimated => visibleAnimated', animate('400ms cubic-bezier(0.86, 0, 0.07, 1)')),
            transition('void => visibleAnimated, visibleAnimated => void',
                animate('400ms cubic-bezier(0.86, 0, 0.07, 1)'))
        ])
    ]
})
export class PinItTrackWebAppMenuItemComponent extends BaseComponent {

    private menuResetSubscription: Subscription;
    private menuSourceSubscription: Subscription;

    constructor(private readonly servicesAccessor: PinItTrackFrontendServicesAccessor, public router: Router) {
        super(servicesAccessor.coreServicesAccessor.applicationService);

        this.applicationServiceExt = this.servicesAccessor.coreServicesAccessor.applicationService;

        this.menuSourceSubscription = this.servicesAccessor.coreServicesAccessor.applicationService.menuSource$.subscribe(key => {
            // deactivate current active menu
            if (this.isActive && this.key !== key && key.indexOf(this.key) !== 0) {
                this.isActive = false;
            }
        });

        this.menuResetSubscription = this.servicesAccessor.coreServicesAccessor.applicationService.resetSource$.subscribe(() => {
            this.isActive = false;
        });

        this.router.events.pipe(filter(event => event instanceof NavigationEnd))
            .subscribe(params => {
                if (this.servicesAccessor.coreServicesAccessor.applicationService.isSidebarSlim) {
                    this.isActive = false;
                } else {
                    if (this.menuItem.routerLink) {
                        this.updateActiveStateFromRoute();
                    } else {
                        this.isActive = false;
                    }
                }
            });
    }

    public readonly applicationServiceExt: IPinItTrackFrontendApplicationService = null;

    public isActive: boolean;

    @Input()
    public isRoot: boolean;

    @Input()
    public menuItem: any;

    @Input()
    public menuItemIndex: number;

    @Input()
    public parentKey: string;

    public key: string;

    protected override async doOnInitAsync(): Promise<void> {
        if (!(this.applicationServiceExt.isSidebarSlim) && this.menuItem.routerLink) {
            this.updateActiveStateFromRoute();
        }

        this.key = this.parentKey ? this.parentKey + '-' + this.menuItemIndex : String(this.menuItemIndex);
    }

    private updateActiveStateFromRoute() {
        this.isActive = this.router.isActive(this.menuItem.routerLink[0], this.menuItem.items ? false : true);
    }

    public itemClick(event: Event): any {
        if (UtilsMisc.isNullOrUndefined(this.menuItem.command) === true) {
            this.servicesAccessor.coreServicesAccessor.applicationService.navigateToAsync(this.menuItem.routerLink[0]);
        }

        // avoid processing disabled items
        if (this.menuItem.disabled) {
            event.preventDefault();
            return true;
        }

        // navigate with hover in horizontal mode
        if (this.isRoot) {
            this.applicationServiceExt.isMenuHoverActive = !this.applicationServiceExt.isMenuHoverActive;
        }

        // notify other items
        this.applicationServiceExt.onMenuStateChange(this.key);

        // execute command
        if (this.menuItem.command) {
            this.menuItem.command({ originalEvent: event, item: this.menuItem });
        }

        // toggle active state
        if (this.menuItem.items) {
            this.isActive = !this.isActive;
        } else {
            // activate item
            this.isActive = true;

            // reset horizontal and slim menu
            if (this.applicationServiceExt.isSidebarSlim) {
                this.applicationServiceExt.resetMenu();
                this.applicationServiceExt.isMenuHoverActive = false;
            }

            // In static mode the menu stays open when navigating; only overlay and
            // slim modes collapse it. The mobile menu is closed in any case.
            if (this.applicationServiceExt.isSidebarStatic !== true) {
                this.applicationServiceExt.isMenuActive = false;
            }

            this.applicationServiceExt.isMobileMenuActive = false;
        }

        this.removeActiveInk(event);
    }

    public onMouseEnter() {
        // activate item on hover
        if (this.isRoot && (!this.menuItem.routerLink || this.menuItem.items) && (this.applicationServiceExt.isSidebarSlim) && this.applicationServiceExt.isDesktop) {
            if (this.applicationServiceExt.isMenuHoverActive) {
                this.applicationServiceExt.onMenuStateChange(this.key);
                this.isActive = true;
            }
        }
    }

    public removeActiveInk(event: Event) {
        let currentTarget = (event.currentTarget as HTMLElement);
        setTimeout(() => {
            if (currentTarget) {
                let activeInk = currentTarget.querySelector('.p-ink-active');
                if (activeInk) {
                    if (activeInk.classList)
                        activeInk.classList.remove('p-ink-active');
                    else
                        activeInk.className = activeInk.className.replace(new RegExp('(^|\\b)' + 'p-ink-active'.split(' ').join('|') + '(\\b|$)', 'gi'), ' ');
                }
            }
        }, 401);
    }

    protected override async doOnDestroyAsync(): Promise<void> {
        if (this.menuSourceSubscription) {
            this.menuSourceSubscription.unsubscribe();
        }

        if (this.menuResetSubscription) {
            this.menuResetSubscription.unsubscribe();
        }
    }

}
